import {
  WalletReportConfig,
  WalletReportFilters,
  WalletReportFormat,
  WalletReportType,
  WalletReportsCatalog,
  defaultColumnsConfig,
  emptyReportFilters,
} from 'src/app/models/WalletReports';

/**
 * Tradução entre o estado do configurador de Relatórios e a query string da rota
 * `/wallet/relatorios` (M7, task 17tm466nz8b) — é o que faz o botão "Exportar" do
 * Extrato, o "Exportar este lote" do painel e o redirecionamento das rotas antigas
 * chegarem em Relatórios já filtrados, e o que faz a URL ser compartilhável.
 *
 * Nomes de parâmetro em português, como o resto da query string do painel
 * (`?recarga=1` no `my-wallet`). `tipo`/`formato` usam os rótulos que aparecem na
 * tela; a tradução para as chaves do contrato (`dispatches`, `csv_br`…) fica aqui.
 */

export type WalletReportBanner = 'extrato' | 'lote' | 'rota-antiga' | null;

/** Rótulo de URL ↔ chave do contrato. Mantém a URL legível sem acoplar à forma do back. */
const TIPO_TO_KEY: { [key: string]: WalletReportType } = {
  disparos: 'dispatches',
  lancamentos: 'entries',
  recargas: 'recharges',
  consumo: 'consumption',
};
const KEY_TO_TIPO: { [key in WalletReportType]: string } = {
  dispatches: 'disparos',
  entries: 'lancamentos',
  recharges: 'recargas',
  consumption: 'consumo',
};

const FORMATO_TO_KEY: { [key: string]: WalletReportFormat } = { csv: 'csv_br', xlsx: 'xlsx' };
const KEY_TO_FORMATO: { [key in WalletReportFormat]: string } = { csv_br: 'csv', xlsx: 'xlsx' };

export interface WalletReportQueryParams {
  tipo?: string;
  canal?: string;
  de?: string;
  ate?: string;
  servico?: string;
  tipo_lancamento?: string;
  lancamento?: string;
  formato?: string;
  origem?: string;
  rota?: string;
}

/** `tipo=disparos&canal=sms&...` → objeto plano, pronto para `router.navigate([], {queryParams})`. */
export function reportConfigToQuery(config: WalletReportConfig, banner: WalletReportBanner = null): WalletReportQueryParams {
  const params: WalletReportQueryParams = { tipo: KEY_TO_TIPO[config.type], formato: KEY_TO_FORMATO[config.format] };

  if (config.filters.channels.length) { params.canal = config.filters.channels.join(','); }
  if (config.filters.from) { params.de = config.filters.from; }
  if (config.filters.to) { params.ate = config.filters.to; }
  if (config.filters.services.length) { params.servico = config.filters.services.join(','); }
  if (config.filters.entry_types.length) { params.tipo_lancamento = config.filters.entry_types.join(','); }
  if (config.filters.entry_ids.length) { params.lancamento = config.filters.entry_ids.join(','); }
  if (banner) { params.origem = banner; }

  return params;
}

/**
 * Query string → config inicial do configurador. Tipo inválido ou ausente (inclusive
 * `tipo=respostas`, que não existe mais) cai em "disparos" — nunca quebra a tela.
 * Colunas e formato começam no padrão do catálogo; a query só decide tipo e filtros.
 */
export function reportConfigFromQuery(params: WalletReportQueryParams, catalog: WalletReportsCatalog): WalletReportConfig {
  const type = (params.tipo && TIPO_TO_KEY[params.tipo]) || 'dispatches';
  const def = catalog.types.find((item) => item.key === type) || catalog.types[0];
  const format = (params.formato && FORMATO_TO_KEY[params.formato]) || 'csv_br';

  const filters: WalletReportFilters = {
    ...emptyReportFilters(),
    from: params.de || null,
    to: params.ate || null,
    // Restrito ao que o catálogo DESTE tipo aceita: um "canal" vindo de uma rota que não é de
    // Disparos (ex. o Extrato manda `canal` para Lançamentos, que só conhece `services`) não
    // pode virar filtro inválido na prévia/exportação — o back responderia 422.
    channels: restrictToAllowed(splitList(params.canal), def.filters.channels),
    services: restrictToAllowed(splitList(params.servico), def.filters.services),
    entry_types: restrictToAllowed(splitList(params.tipo_lancamento), def.filters.entry_types),
    entry_ids: splitList(params.lancamento),
  };

  return { type: def.key, filters, columns: defaultColumnsConfig(def), format };
}

/** Sem lista permitida (o tipo não declara esse filtro no catálogo), nada passa. */
function restrictToAllowed(values: string[], allowed: string[] | undefined): string[] {
  if (!allowed) { return []; }
  return values.filter((value) => allowed.includes(value));
}

export function queryBanner(params: WalletReportQueryParams): WalletReportBanner {
  return (params.origem as WalletReportBanner) || null;
}

/** "Exportar" no Extrato: tipo certo (recargas para crédito/bônus) + os filtros que estavam na tela. */
export function statementFiltersToReportQuery(filters: {
  service?: string | null;
  entry_type?: string | null;
  from?: string | null;
  to?: string | null;
}): WalletReportQueryParams {
  const isCreditLike = filters.entry_type === 'credit' || filters.entry_type === 'bonus';
  const params: WalletReportQueryParams = { tipo: isCreditLike ? 'recargas' : 'lancamentos', origem: 'extrato' };

  if (filters.service) { params.canal = filters.service; params.servico = filters.service; }
  if (filters.entry_type && !isCreditLike) { params.tipo_lancamento = filters.entry_type; }
  if (filters.from) { params.de = filters.from; }
  if (filters.to) { params.ate = filters.to; }

  return params;
}

/** "Exportar este lote" no painel expansível do extrato → Disparos filtrado por este lançamento. */
export function batchToReportQuery(entryId: string): WalletReportQueryParams {
  return { tipo: 'disparos', lancamento: entryId, origem: 'lote' };
}

export type LegacyWalletScreen = 'messages' | 'responses' | 'recharges';

/**
 * Rotas antigas → Relatórios, conforme o mapa sugerido pelo m7-back
 * (`~/trabalhos/m7-coord.md`, seção 1). `month`/`year` (1-based, como a tela de
 * Mensagens valida) viram o mês inteiro; `date` (rota `/messages-report/:date`)
 * vira um único dia.
 */
export function legacyRouteToReportQuery(
  screen: LegacyWalletScreen,
  params: { month?: string | number; year?: string | number; date?: string } = {}
): WalletReportQueryParams {
  if (screen === 'recharges') {
    return { tipo: 'recargas', origem: 'rota-antiga', rota: 'recarga' };
  }

  const base: WalletReportQueryParams = {
    tipo: 'disparos',
    canal: 'sms',
    origem: 'rota-antiga',
    rota: screen === 'responses' ? 'respostas' : 'mensagens',
  };

  if (params.date) {
    return { ...base, de: params.date, ate: params.date };
  }

  if (params.month && params.year) {
    const month = Number(params.month);
    const year = Number(params.year);
    const from = `${year}-${String(month).padStart(2, '0')}-01`;
    const to = lastDayOfMonth(year, month);
    return { ...base, de: from, ate: to };
  }

  return base;
}

function splitList(value: string | undefined | null): string[] {
  return value ? value.split(',').map((item) => item.trim()).filter(Boolean) : [];
}

function lastDayOfMonth(year: number, month: number): string {
  const day = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
